import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Alert, CardBody, Button, Label, Input, FormFeedback, Form, InputGroup, InputGroupText, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import withRouter from "../../components/Common/withRouter";
import Breadcrumb from "../../components/Common/Breadcrumb";
import avatar from "../../assets/images/users/icon.jpg";
import { editProfile, resetProfileFlag } from "../../store/actions";
import { createSelector } from "reselect";
import { useNavigate } from "react-router-dom";

const UserProfile = () => {
  document.title = "Profile | Onfon Group Legal Dept - React ";
  const dispatch = useDispatch();
  const [userData, setUserData] = useState({ username: "", email: "", _id: "", role: "", phone: "" });
  const [loading, setLoading] = useState(true); // Loading state for API call
  const [apiError, setApiError] = useState(null); // State for API errors
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState(null);

  const navigate = useNavigate();

  const userprofilepage = createSelector(
    (state) => state.profile,
    (state) => ({
      error: state.error,
      success: state.success,
    })
  );

  const { error, success } = useSelector(userprofilepage);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!sessionStorage.getItem("authUser")) {
          throw new Error("User not authenticated");
        }

        const obj = JSON.parse(sessionStorage.getItem("authUser"));
        const token = sessionStorage.getItem("authToken");

        if (!token) {
          throw new Error("Authentication token not found");
        }

        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/${obj._id}`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const userData = await response.json();
          // console.log({userData})
          setUserData({
            name: userData.username || obj.username,
            email: userData.email || obj.email,
            _id: userData._id || obj.uid,
            role: userData.role ||obj.role,
            phone: userData.phone || "",
          });
        } else {
          setApiError(response.message);
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setApiError(err.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        dispatch(resetProfileFlag());
      }, 3000);
    }
  }, [dispatch, success]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: userData.name || "",
      _id: userData._id || "",
      email: userData.email || "",
      phone: userData.phone || "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please Enter Your UserName"),
      email: Yup.string().required("Please Enter Your Email"),
      phone: Yup.string().required("Please Enter Your Mobile Number"),
    }),
    onSubmit: (values) => {
      // console.log({values})
      dispatch(editProfile(values));
      navigate('/userprofile')
    },
  });

  // Password change form validation
  const passwordValidation = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Please Enter Your Current Password"),
      newPassword: Yup.string()
        .required("Please Enter New Password")
        .min(8, "Password must be at least 8 characters"),
      confirmPassword: Yup.string()
        .required("Please Confirm Your Password")
        .oneOf([Yup.ref("newPassword")], "Passwords must match"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
          throw new Error("Authentication token not found");
        }

        const obj = JSON.parse(sessionStorage.getItem("authUser"));
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/${obj._id}`, {
          method: "PATCH",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: userData._id,
            oldPassword: values.oldPassword,
            password: values.newPassword,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setPasswordChangeSuccess(true);
          setPasswordChangeError(null);
          resetForm();
          
          // Close modal after 2 seconds
          setTimeout(() => {
            setPasswordModal(false);
            setPasswordChangeSuccess(false);
          }, 2000);
        } else {
          setPasswordChangeError(data.message || "Failed to change password");
          setPasswordChangeSuccess(false);
        }
      } catch (err) {
        console.error("Error changing password:", err);
        setPasswordChangeError(err.message || "An unexpected error occurred");
        setPasswordChangeSuccess(false);
      }
    },
  });

  const renderRoleSpecificContent = () => {
    switch (userData.role) {
      case "legal":
        return <p>Welcome, Legal Department Member!</p>;
      case "director":
        return <p>Welcome, Director!</p>;
      case "IT":
        return <p>Welcome, IT Specialist!</p>;
      default:
        return <p>Welcome, User!</p>;
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (apiError) {
    return (
      <div className="text-center mt-5">
        <Alert color="danger">{apiError}</Alert>
      </div>
    );
  }

  // Toggle password modal
  const togglePasswordModal = () => {
    setPasswordModal(!passwordModal);
    if (!passwordModal) {
      passwordValidation.resetForm();
      setPasswordChangeSuccess(false);
      setPasswordChangeError(null);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Onfon Legal" breadcrumbItem="Profile" />
          <Row>
            <Col lg="12">
              {error && error ? (
                <Alert color="danger">
                  <div>{error}</div>
                </Alert>
              ) : null}
              {success ? (
                <Alert color="success">
                  <div>{success}</div>
                </Alert>
              ) : null}
              <Card>
                <CardBody>
                  <div className="d-flex">
                    <div className="ms-3">
                      <img
                        src={avatar}
                        alt=""
                        className="avatar-md rounded-circle img-thumbnail"
                      />
                    </div>
                    <div className="flex-grow-1 align-self-center ms-3">
                      <div className="text-muted">
                        <h5>{userData.name}</h5>
                        <p className="mb-1"><span className="fw-bold">Email: </span>{userData.email}</p>
                        <p className="mb-1"><span className="fw-bold">Phone: </span>{userData.phone}</p>
                        <p className="mb-0"><span className="fw-bold">User Id: </span>{userData._id}</p>
                        <p className="mb-4"> </p>
                        {renderRoleSpecificContent()}
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <h4 className="card-title mb-4">Change User Details</h4>
          <Card>
            <CardBody>
              <Form
                className="form-horizontal"
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <div className="form-group">
                  <Label className="form-label">Username</Label>
                  <Input
                    name="username"
                    className="form-control"
                    placeholder="Enter User Name"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.username || ""}
                    invalid={
                      validation.touched.username && validation.errors.username
                        ? true
                        : false
                    }
                  />
                  {validation.touched.username && validation.errors.username ? (
                    <FormFeedback type="invalid">
                      <div>{validation.errors.username}</div>
                    </FormFeedback>
                  ) : null}

                  <Label className="form-label">Email</Label>
                  <Input
                    name="email"
                    className="form-control"
                    placeholder="Enter User Email"
                    type="email"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email || ""}
                    invalid={
                      validation.touched.email && validation.errors.email
                        ? true
                        : false
                    }
                  />
                  {validation.touched.email && validation.errors.email ? (
                    <FormFeedback type="invalid">
                      <div>{validation.errors.email}</div>
                    </FormFeedback>
                  ) : null}

                  <Label className="form-label">Phone</Label>
                  <Input
                    name="phone"
                    className="form-control"
                    placeholder="Enter Phone Number"
                    type="tel"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.phone || ""}
                    invalid={
                      validation.touched.phone && validation.errors.phone
                        ? true
                        : false
                    }
                  />
                  {validation.touched.phone && validation.errors.phone ? (
                    <FormFeedback type="invalid">
                      <div>{validation.errors.phone}</div>
                    </FormFeedback>
                  ) : null}

                  <Input name="_id" value={userData._id} type="hidden" />  
                </div>
                <div className="text-center mt-4">
                  <Button type="submit" color="danger" className="me-2">
                    Update Details
                  </Button>
                  <Button type="button" color="primary" onClick={togglePasswordModal}>
                    Change Password
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Password Change Modal */}
      <Modal isOpen={passwordModal} toggle={togglePasswordModal} centered={true}>
        <ModalHeader toggle={togglePasswordModal}>Change Password</ModalHeader>
        <ModalBody>
          {passwordChangeSuccess && (
            <Alert color="success">
              Password changed successfully!
            </Alert>
          )}
          {passwordChangeError && (
            <Alert color="danger">
              {passwordChangeError}
            </Alert>
          )}
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              passwordValidation.handleSubmit();
              return false;
            }}
          >
            <div className="mb-3">
              <Label for="oldPassword" className="form-label">Current Password</Label>
              <InputGroup>
                <Input
                  id="oldPassword"
                  name="oldPassword"
                  type={showOldPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  onChange={passwordValidation.handleChange}
                  onBlur={passwordValidation.handleBlur}
                  value={passwordValidation.values.oldPassword}
                  invalid={
                    passwordValidation.touched.oldPassword && passwordValidation.errors.oldPassword
                      ? true
                      : false
                  }
                />
                <InputGroupText onClick={() => setShowOldPassword(!showOldPassword)} style={{ cursor: "pointer" }}>
                  <i className={`fas ${showOldPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                </InputGroupText>
                {passwordValidation.touched.oldPassword && passwordValidation.errors.oldPassword ? (
                  <FormFeedback type="invalid">
                    {passwordValidation.errors.oldPassword}
                  </FormFeedback>
                ) : null}
              </InputGroup>
            </div>
            
            <div className="mb-3">
              <Label for="newPassword" className="form-label">New Password</Label>
              <InputGroup>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  onChange={passwordValidation.handleChange}
                  onBlur={passwordValidation.handleBlur}
                  value={passwordValidation.values.newPassword}
                  invalid={
                    passwordValidation.touched.newPassword && passwordValidation.errors.newPassword
                      ? true
                      : false
                  }
                />
                <InputGroupText onClick={() => setShowNewPassword(!showNewPassword)} style={{ cursor: "pointer" }}>
                  <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                </InputGroupText>
                {passwordValidation.touched.newPassword && passwordValidation.errors.newPassword ? (
                  <FormFeedback type="invalid">
                    {passwordValidation.errors.newPassword}
                  </FormFeedback>
                ) : null}
              </InputGroup>
            </div>
            
            <div className="mb-3">
              <Label for="confirmPassword" className="form-label">Confirm New Password</Label>
              <InputGroup>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  onChange={passwordValidation.handleChange}
                  onBlur={passwordValidation.handleBlur}
                  value={passwordValidation.values.confirmPassword}
                  invalid={
                    passwordValidation.touched.confirmPassword && passwordValidation.errors.confirmPassword
                      ? true
                      : false
                  }
                />
                <InputGroupText onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ cursor: "pointer" }}>
                  <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                </InputGroupText>
                {passwordValidation.touched.confirmPassword && passwordValidation.errors.confirmPassword ? (
                  <FormFeedback type="invalid">
                    {passwordValidation.errors.confirmPassword}
                  </FormFeedback>
                ) : null}
              </InputGroup>
            </div>
            
            <div className="text-end">
              <Button color="secondary" onClick={togglePasswordModal} className="me-2">
                Cancel
              </Button>
              <Button color="outline-primary" type="submit">
                Change Password
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(UserProfile);
